import { DialogContent } from './ui/DialogContent';
import React, { useState } from 'react';
import { Typography, TextField, Button } from '@mui/material';
import { ResponsiveDialog } from './ui/ResponsiveDialog';
import { DialogHeader } from './ui/DialogHeader';
import { DialogActions } from './ui/DialogActions';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';

interface FindReplaceKeywordsModalProps {
  scopeKey?: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (findRegex: RegExp, replaceText: string) => void;
}

const FindReplaceKeywordsModal: React.FC<FindReplaceKeywordsModalProps> = ({
  scopeKey,
  open,
  onClose,
  onConfirm,
}) => {
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const discard = () => {
    setFindText('');
    setReplaceText('');
  };
  const { requestClose, runAfterSave } = useUnsavedChangesGuard({
    scopeKey,
    open,
    dirty: findText !== '' || replaceText !== '',
    onDiscard: discard,
    onClose,
  });
  const close = () => {
    void requestClose();
  };

  const handleSubmit = () => {
    if (findText.trim()) {
      // Create a case-insensitive regex pattern
      const regex = new RegExp(findText.trim(), 'gi');
      runAfterSave(() => onConfirm(regex, replaceText.trim()));
      setFindText('');
      setReplaceText('');
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={close}
      aria-labelledby="find-replace-title"
    >
      <DialogHeader
        id="find-replace-title"
        title="Find and Replace Keywords"
        onClose={close}
      />
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Find and replace text in keywords of selected items (case-insensitive)
        </Typography>
        <TextField
          fullWidth
          label="Find"
          value={findText}
          onChange={(e) => setFindText(e.target.value)}
          placeholder="Enter text to find..."
        />
        <TextField
          fullWidth
          label="Replace with"
          value={replaceText}
          onChange={(e) => setReplaceText(e.target.value)}
          placeholder="Enter replacement text..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!findText.trim()}
        >
          Replace
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default FindReplaceKeywordsModal;
